import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, FilePlus2, FolderKanban, UserCircle, ArrowRight, Plus,
  Clock4, CheckCircle2, Search as SearchIcon, ChevronRight, Check, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "../../components/DashboardLayout";
import { FORM_OPTIONS } from "../../lib/mockData";
import { auth, api } from "../../lib/api";

const STATUS_STYLES = {
  amber: "bg-amber-100 text-amber-700",
  teal: "bg-[#D5EAE6] text-[#0B5447]",
  lime: "bg-[#EAF6D6] text-[#466B1F]",
  green: "bg-[#CDEFD4] text-[#1B6B34]",
};
const PATENT_STAGES = ["Submitted", "Comparing Quotes", "Filed at IPO", "Under Prosecution", "Granted"];
const DEFAULT_AVATAR = "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&q=85";

export default function CustomerDashboard() {
  const [active, setActive] = useState("overview");
  const [user, setUser] = useState(auth.user);
  const [patents, setPatents] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [me, pats] = await Promise.all([api.me(), api.myPatents()]);
      setUser(me); auth.updateUser(me);
      setPatents(pats);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const nav = [
    { id: "overview", label: "Overview", desc: "Your patent activity at a glance", icon: LayoutDashboard },
    { id: "submit", label: "Submit Innovation", desc: "Start a new patent request", icon: FilePlus2 },
    { id: "patents", label: "My Patents", desc: "Track all your submissions", icon: FolderKanban, badge: patents.length || undefined },
    { id: "profile", label: "My Profile", desc: "Manage your account details", icon: UserCircle },
  ];

  return (
    <DashboardLayout
      role="customer" roleLabel="Customer Portal" nav={nav} active={active} setActive={setActive}
      user={{ name: user?.name || "Innovator", sub: user?.company || user?.phone || "", avatar: DEFAULT_AVATAR }}
      logoutTo="/portal/login"
    >
      {loading ? (
        <div className="flex items-center justify-center h-96 text-[#5A6B66]"><Loader2 className="animate-spin mr-2"/> Loading…</div>
      ) : (
        <>
          {active === "overview" && <Overview user={user} patents={patents} setActive={setActive} />}
          {active === "submit" && <SubmitPanel onSubmit={async (payload) => { await api.submitPatent(payload); toast.success("Innovation submitted — awaiting quotes"); await refresh(); setActive("patents"); }} />}
          {active === "patents" && <PatentsPanel patents={patents} />}
          {active === "profile" && <ProfilePanel user={user} onSave={async (data) => { const u = await api.updateCustomerProfile(data); setUser(u); auth.updateUser(u); toast.success("Profile updated"); }} />}
        </>
      )}
    </DashboardLayout>
  );
}

function Overview({ user, patents, setActive }) {
  const stats = [
    { label: "Active patents", value: patents.filter(p => p.stage !== "Granted").length, icon: Clock4 },
    { label: "Granted", value: patents.filter(p => p.stage === "Granted").length, icon: CheckCircle2 },
    { label: "Comparing quotes", value: patents.filter(p => p.stage === "Comparing Quotes").length, icon: SearchIcon },
    { label: "Total filed", value: patents.length, icon: FolderKanban },
  ];
  return (
    <div className="space-y-6" data-testid="customer-overview">
      <div className="rounded-2xl surface-deep text-white p-7 lg:p-8 flex items-center justify-between gap-6 flex-wrap">
        <div>
          <div className="chip chip-gold inline-flex mb-3">Innovator · +91 {user?.phone}</div>
          <h2 className="font-display font-extrabold text-3xl mb-1">Welcome back, {(user?.name || "Innovator").split(" ")[0]}</h2>
          <p className="text-white/70">{patents.length ? `You have ${patents.length} active brief${patents.length !== 1 ? "s" : ""}.` : "Start by submitting your first innovation."}</p>
        </div>
        <button onClick={() => setActive("submit")} className="btn-accent" data-testid="overview-new-patent"><Plus size={16}/> New patent request</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="card p-6">
            <div className="w-11 h-11 rounded-xl surface-sage flex items-center justify-center text-[#0E6E5E] mb-4"><s.icon size={20}/></div>
            <div className="font-display font-extrabold text-4xl text-[#0A2520]">{s.value}</div>
            <div className="text-sm text-[#5A6B66] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-xl text-[#0A2520]">Recent activity</h3>
          <button onClick={() => setActive("patents")} className="text-sm font-semibold text-[#0E6E5E] inline-flex items-center gap-1">View all <ChevronRight size={14}/></button>
        </div>
        <div className="space-y-3">
          {patents.slice(0, 3).map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl surface-sage">
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-[#0A2520] truncate">{p.title}</div>
                <div className="text-xs text-[#5A6B66] mt-0.5">{p.type} · {p.attorney_name || "Awaiting attorney"}</div>
              </div>
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${STATUS_STYLES[p.status_color] || STATUS_STYLES.lime}`}>{p.stage}</span>
            </div>
          ))}
          {patents.length === 0 && <div className="text-sm text-[#5A6B66] p-3">No submissions yet. Start with "New patent request".</div>}
        </div>
      </div>
    </div>
  );
}

function SubmitPanel({ onSubmit }) {
  const [ptype, setPtype] = useState("Utility Patent");
  const [entity, setEntity] = useState("Startup (DPIIT)");
  const [industry, setIndustry] = useState(FORM_OPTIONS.industries[0]);
  const [city, setCity] = useState(FORM_OPTIONS.cities[0]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Add a working title"); return; }
    setBusy(true);
    try { await onSubmit({ title, type: ptype, entity, industry, city, description }); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="customer-submit">
      <div className="card p-7 lg:p-9">
        <h2 className="font-display font-bold text-2xl text-[#0A2520] mb-2">Submit a new innovation</h2>
        <p className="text-[#5A6B66] mb-8">Share a short brief — attorneys will send tentative quotes within hours.</p>

        <Label>What kind of protection?</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-6">
          {FORM_OPTIONS.patentTypes.map((t) => (
            <button key={t} onClick={() => setPtype(t)} className={`p-3 rounded-xl border text-sm font-semibold transition ${ptype === t ? "border-[#0E6E5E] surface-sage text-[#0B5447]" : "border-[#E1E8E2] bg-white text-[#3D4F4A] hover:border-[#7CC242]"}`}>{t}</button>
          ))}
        </div>

        <Label>I'm filing as</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-6">
          {FORM_OPTIONS.entityTypes.map((t) => (
            <button key={t} onClick={() => setEntity(t)} className={`p-3 rounded-xl border text-sm font-semibold transition ${entity === t ? "border-[#0E6E5E] surface-sage text-[#0B5447]" : "border-[#E1E8E2] bg-white text-[#3D4F4A] hover:border-[#7CC242]"}`}>{t}</button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-5 mb-6">
          <div><Label>Industry / Domain</Label><select className="input" value={industry} onChange={(e) => setIndustry(e.target.value)}>{FORM_OPTIONS.industries.map(i => <option key={i}>{i}</option>)}</select></div>
          <div><Label>City</Label><select className="input" value={city} onChange={(e) => setCity(e.target.value)}>{FORM_OPTIONS.cities.map(i => <option key={i}>{i}</option>)}</select></div>
        </div>

        <Label>Working title</Label>
        <input className="input mb-6" placeholder="e.g. Self-cooling packaging for perishables" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="submit-title"/>

        <Label>Brief description</Label>
        <textarea rows={5} className="input resize-none leading-relaxed mb-6" placeholder="Describe your innovation in 50–500 words. Avoid disclosing trade secrets." value={description} onChange={(e) => setDescription(e.target.value)} data-testid="submit-description"/>

        <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="customer-submit-btn">
          {busy ? <Loader2 size={16} className="animate-spin"/> : <>Request quotes <ArrowRight size={16}/></>}
        </button>
      </div>
    </div>
  );
}

function PatentsPanel({ patents }) {
  return (
    <div className="space-y-4" data-testid="customer-patents">
      {patents.length === 0 && (
        <div className="card p-8 text-center text-[#5A6B66]">No patents yet. Submit your first innovation to get started.</div>
      )}
      {patents.map((p) => (
        <div key={p.id} className="card p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-display font-bold text-lg text-[#0A2520]">{p.title}</h3>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[p.status_color] || STATUS_STYLES.lime}`}>{p.stage}</span>
              </div>
              <div className="text-sm text-[#5A6B66]">{p.type} · {p.industry || "—"} · submitted {new Date(p.created_at).toLocaleDateString()}</div>
            </div>
            {p.quote && <div className="text-right"><div className="text-xs text-[#5A6B66]">Quote</div><div className="font-display font-bold text-xl text-[#0A2520]">₹{p.quote.toLocaleString('en-IN')}</div></div>}
          </div>

          <div className="flex items-center mb-5">
            {PATENT_STAGES.map((st, i) => (
              <React.Fragment key={st}>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= (p.stage_index || 0) ? "bg-[#0E6E5E] text-white" : "bg-[#E1E8E2] text-[#5A6B66]"}`}>
                    {i < (p.stage_index || 0) ? <Check size={13}/> : i + 1}
                  </div>
                  <div className={`text-[10px] mt-1.5 text-center w-16 leading-tight ${i <= (p.stage_index || 0) ? "text-[#0A2520] font-semibold" : "text-[#5A6B66]"}`}>{st}</div>
                </div>
                {i < PATENT_STAGES.length - 1 && <div className={`flex-1 h-0.5 mb-5 ${i < (p.stage_index || 0) ? "bg-[#0E6E5E]" : "bg-[#E1E8E2]"}`}/>}
              </React.Fragment>
            ))}
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap pt-4 border-t border-[#F0F4F0]">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[#5A6B66]">Attorney:</span>
              {p.attorney_id
                ? <Link to={`/attorneys/${p.attorney_id}`} className="font-semibold text-[#0E6E5E] hover:underline">{p.attorney_name}</Link>
                : <span className="text-[#5A6B66]">Awaiting admin assignment</span>}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock4 size={13} className="text-[#7CC242]"/>
              <span className="text-[#3D4F4A] font-medium">{p.next_action}</span>
            </div>
            {p.stage === "Comparing Quotes"
              ? <Link to="/compare" className="btn-primary text-sm">Compare quotes <ArrowRight size={14}/></Link>
              : <button className="btn-outline text-sm">View details</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProfilePanel({ user, onSave }) {
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    company: user?.company || "",
    entity: user?.entity || "Startup (DPIIT)",
    city: user?.city || "Bengaluru",
    industry: user?.industry || "Software & AI",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await onSave(form); } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="customer-profile">
      <div className="card p-7">
        <div className="flex items-center gap-5 mb-8">
          <img src={DEFAULT_AVATAR} alt={form.name} className="w-20 h-20 rounded-2xl object-cover"/>
          <div>
            <h2 className="font-display font-bold text-2xl text-[#0A2520]">{form.name || "New Innovator"}</h2>
            <p className="text-[#5A6B66]">{form.company || "—"} · +91 {user?.phone}</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div><Label>Full name</Label><input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name"/></div>
          <div><Label>Mobile</Label><input className="input bg-[#F5F8F5]" value={`+91 ${user?.phone || ""}`} disabled/></div>
          <div><Label>Email</Label><input className="input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="profile-email"/></div>
          <div><Label>Company / Organisation</Label><input className="input" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="profile-company"/></div>
          <div><Label>Entity type</Label><select className="input" value={form.entity} onChange={(e) => set("entity", e.target.value)}>{FORM_OPTIONS.entityTypes.map(e => <option key={e}>{e}</option>)}</select></div>
          <div><Label>City</Label><select className="input" value={form.city} onChange={(e) => set("city", e.target.value)}>{FORM_OPTIONS.cities.map(c => <option key={c}>{c}</option>)}</select></div>
        </div>
        <div className="flex gap-3 mt-8 pt-6 border-t border-[#F0F4F0]">
          <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="profile-save">
            {busy ? <Loader2 size={16} className="animate-spin"/> : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">{children}</label>;
}
